import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { visitNoteSchema } from '@/components/FormBuilder';
import { useInstitutionsStore } from '@/stores/institutions';
import {
  ESignatureSettingsRequiredBy,
  ESignatureSettingsType,
  TFormBuilderEditSchemaCamel,
  TVisitNotesSchemaCamel,
} from '@/types';

const defaultSignatureSettings = {
  type: ESignatureSettingsType.NO_SIGNATURE,
  requiredBy: ESignatureSettingsRequiredBy.PROVIDER,
};

/**
 * Fills in signature settings for templates saved before visit notes had any.
 * @param data - The stored template data.
 * @returns The data with signature settings guaranteed.
 */
const withSignatureSettings = (
  data: TVisitNotesSchemaCamel,
): TFormBuilderEditSchemaCamel => ({
  ...data,
  signatureSettings: data.signatureSettings ?? defaultSignatureSettings,
});

export const useVisitNoteTemplate = () => {
  const { formBuilderId } = useParams();

  const { setDraftFormBuilderTemplate, draft, getVisitNoteTemplateById } =
    useInstitutionsStore((state) => ({
      getVisitNoteTemplateById: state.getVisitNoteTemplateById,
      setDraftFormBuilderTemplate: state.setDraftVisitNotesTemplate,
      draft: state.draftVisitNotesTemplate,
    }));

  const methods = useForm<TFormBuilderEditSchemaCamel>({
    resolver: zodResolver(visitNoteSchema),
    defaultValues: draft ?? {
      title: '',
      fields: [],
      signatureSettings: defaultSignatureSettings,
    },
    mode: 'onSubmit',
  });

  const { reset } = methods;

  useEffect(() => {
    if (formBuilderId === 'empty' || !formBuilderId || !!draft) return;
    const callback = async () => {
      const value = getVisitNoteTemplateById(formBuilderId);
      if (!value) return;
      const data = withSignatureSettings(value.data);
      setDraftFormBuilderTemplate(data);
      reset(data);
    };
    void callback();
  }, [
    draft,
    formBuilderId,
    getVisitNoteTemplateById,
    reset,
    setDraftFormBuilderTemplate,
  ]);

  useEffect(
    () => () => setDraftFormBuilderTemplate(null),
    [setDraftFormBuilderTemplate],
  );

  return { methods };
};
